import { assertNever } from '@deltasierra/shared';
import { alpha, Fade } from '@material-ui/core';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { DSAlertProps, DSPopperProps, DSAlert, DSPopper } from '../../../mui';

const StyledAlert = styled(DSAlert)<{ $maxWidth?: number }>`
    ${props =>
        props.$maxWidth &&
        `
        max-width: ${props.$maxWidth}px;
    `};

    box-shadow: ${props => props.theme.shadows[2]};

    background-color: ${props => alpha(props.theme.palette.background.paper, 0.95)};
`;

function maxWidthToNumber(maxWidth: HelpPopperPopperMaxWidthType): number | undefined {
    switch (maxWidth) {
        case 'none':
            return undefined;
        case 'small':
            return 200;
        case 'medium':
            return 250;
        case 'large':
            return 500;
        default:
            return assertNever(maxWidth);
    }
}

export type HelpPopperPopperMaxWidthType = 'large' | 'medium' | 'none' | 'small';

export type HelpPopperPopperProps = {
    anchorEle?: DSPopperProps['anchorEl'];
    children: React.ReactNode;
    clickOutsideRef: React.RefObject<any>;
    icon?: DSAlertProps['icon'];
    maxWidth: HelpPopperPopperMaxWidthType;
    modifiers?: DSPopperProps['modifiers'];
    open: DSPopperProps['open'];
    placement?: DSPopperProps['placement'];
    transitionTimeout?: number;
};

export function HelpPopperPopper({
    anchorEle,
    children,
    clickOutsideRef,
    icon,
    maxWidth,
    modifiers,
    open,
    placement,
    transitionTimeout = 250,
}: HelpPopperPopperProps): JSX.Element {
    const maxWidthNumber = React.useMemo(() => maxWidthToNumber(maxWidth), [maxWidth]);
    const theme = useTheme();

    return (
        <DSPopper
            anchorEl={anchorEle}
            modifiers={modifiers}
            open={open}
            placement={placement}
            style={{ zIndex: theme.zIndex.tooltip }}
            transition
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={transitionTimeout}>
                    <StyledAlert $maxWidth={maxWidthNumber} icon={icon} innerRef={clickOutsideRef} severity="info">
                        {children}
                    </StyledAlert>
                </Fade>
            )}
        </DSPopper>
    );
}
HelpPopperPopper.displayName = 'HelpPopperPopper';
